export const INTERVIEW_VAD_POLICY = {
  speechStartRms: 0.020,
  speechContinueRms: 0.017,
  hysteresisRms: 0.003,
  minimumSpeechWallClockMs: 900,
  minimumRecordingDurationMs: 2600,
  endSilenceMs: 2800,
  pauseUiMs: 650,
  initialNoSpeechMs: 10000,
  clearSpeechTimeoutMs: 9000,
  hardMaximumRecordingMs: 120000,
  minimumCumulativeSpeechMs: 700,
  minimumCumulativeStrongSpeechMs: 180,
  minimumSpeechRatio: 0.06,
  veryLowPeakRms: 0.016,
  veryLowAverageRms: 0.010,
  peakDropRatio: 0.55,
  maximumCreditedSampleIntervalMs: 120,
  firstSampleDurationMs: 16,
  minimumAudioBlobBytes: 8000,
  postTtsListeningDelayMs: 1500,
} as const;

export const INTERVIEW_VAD_AMBIENT_PROFILE = {
  maxRetainedSamples: 256,
  minimumSufficientSamples: 12,
  minimumSufficientDurationMs: 200,
} as const;

export type InterviewVadAmbientProfile = {
  sampleCount: number;
  durationMs: number;
  minRms: number | null;
  averageRms: number | null;
  medianRms: number | null;
  p90Rms: number | null;
  maxRms: number | null;
  startThreshold: number;
  continuationThreshold: number;
  startMarginOverP90: number | null;
  sufficientSamples: boolean;
  speechStarted: boolean;
};

export type InterviewVadAmbientState = {
  createdAt: number;
  sampleCount: number;
  rmsSum: number;
  minRms: number | null;
  maxRms: number | null;
  firstSampleAt: number | null;
  lastSampleAt: number | null;
  retainedSamples: number[];
  nextReplacementIndex: number;
  maxRetainedSamples: number;
  frozen: boolean;
  profile: InterviewVadAmbientProfile | null;
};

export const createInterviewVadAmbientState = (
  createdAt: number,
  maxRetainedSamples = INTERVIEW_VAD_AMBIENT_PROFILE.maxRetainedSamples,
): InterviewVadAmbientState => ({
  createdAt,
  sampleCount: 0,
  rmsSum: 0,
  minRms: null,
  maxRms: null,
  firstSampleAt: null,
  lastSampleAt: null,
  retainedSamples: [],
  nextReplacementIndex: 0,
  maxRetainedSamples: Math.max(1, Math.floor(Number(maxRetainedSamples) || 1)),
  frozen: false,
  profile: null,
});

export const observeInterviewVadAmbientSample = (
  currentState: InterviewVadAmbientState,
  input: { rms: number; timestampMs: number },
): InterviewVadAmbientState => {
  if (currentState.frozen) return currentState;

  const rms = Math.max(0, Number(input.rms) || 0);
  const timestampMs = Number(input.timestampMs);
  const retainedSamples = [...currentState.retainedSamples];
  let nextReplacementIndex = currentState.nextReplacementIndex;
  if (retainedSamples.length < currentState.maxRetainedSamples) {
    retainedSamples.push(rms);
  } else {
    retainedSamples[nextReplacementIndex] = rms;
    nextReplacementIndex = (nextReplacementIndex + 1) % currentState.maxRetainedSamples;
  }

  return {
    ...currentState,
    sampleCount: currentState.sampleCount + 1,
    rmsSum: currentState.rmsSum + rms,
    minRms: currentState.minRms === null ? rms : Math.min(currentState.minRms, rms),
    maxRms: currentState.maxRms === null ? rms : Math.max(currentState.maxRms, rms),
    firstSampleAt: currentState.firstSampleAt === null ? timestampMs : currentState.firstSampleAt,
    lastSampleAt: timestampMs,
    retainedSamples,
    nextReplacementIndex,
  };
};

const roundAmbientMeasurement = (value: number | null, precision = 6) => {
  if (value === null || !Number.isFinite(value)) return null;
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

const ambientPercentile = (samples: number[], percentile: number) => {
  if (!samples.length) return null;
  const sorted = [...samples].sort((left, right) => left - right);
  const position = (sorted.length - 1) * percentile;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);
  const lower = sorted[lowerIndex];
  const upper = sorted[upperIndex];
  if (lowerIndex === upperIndex) return lower;
  return lower + ((upper - lower) * (position - lowerIndex));
};

export const freezeInterviewVadAmbientProfile = (
  currentState: InterviewVadAmbientState,
  input: { timestampMs: number; speechStarted: boolean },
): { state: InterviewVadAmbientState; profile: InterviewVadAmbientProfile } => {
  if (currentState.profile) {
    return { state: currentState, profile: currentState.profile };
  }

  const durationMs = Math.max(0, Number(input.timestampMs) - currentState.createdAt);
  const averageRms = currentState.sampleCount > 0
    ? currentState.rmsSum / currentState.sampleCount
    : null;
  const medianRms = ambientPercentile(currentState.retainedSamples, 0.5);
  const p90Rms = ambientPercentile(currentState.retainedSamples, 0.9);
  const roundedP90Rms = roundAmbientMeasurement(p90Rms);
  const profile: InterviewVadAmbientProfile = {
    sampleCount: currentState.sampleCount,
    durationMs: roundAmbientMeasurement(durationMs, 3) || 0,
    minRms: roundAmbientMeasurement(currentState.minRms),
    averageRms: roundAmbientMeasurement(averageRms),
    medianRms: roundAmbientMeasurement(medianRms),
    p90Rms: roundedP90Rms,
    maxRms: roundAmbientMeasurement(currentState.maxRms),
    startThreshold: INTERVIEW_VAD_POLICY.speechStartRms,
    continuationThreshold: INTERVIEW_VAD_POLICY.speechContinueRms,
    startMarginOverP90: roundedP90Rms === null
      ? null
      : roundAmbientMeasurement(INTERVIEW_VAD_POLICY.speechStartRms - roundedP90Rms),
    sufficientSamples: currentState.sampleCount >= INTERVIEW_VAD_AMBIENT_PROFILE.minimumSufficientSamples
      && durationMs >= INTERVIEW_VAD_AMBIENT_PROFILE.minimumSufficientDurationMs,
    speechStarted: Boolean(input.speechStarted),
  };
  const state = {
    ...currentState,
    frozen: true,
    profile,
  };
  return { state, profile };
};

export type InterviewVadPolicyTerminationReason =
  | 'end_silence'
  | 'initial_no_speech'
  | 'clear_speech_timeout'
  | 'hard_max_duration';

export type InterviewVadPolicyEvent =
  | { type: 'speech_started'; timestampMs: number }
  | { type: 'speech_paused'; timestampMs: number }
  | { type: 'speech_continued'; timestampMs: number }
  | {
      type: 'terminate';
      timestampMs: number;
      reason: InterviewVadPolicyTerminationReason;
    };

export type InterviewVadPolicyState = {
  createdAt: number;
  peakRms: number;
  averageRms: number;
  sampleCount: number;
  cumulativeSpeechMs: number;
  cumulativeStrongSpeechMs: number;
  lastSampleAt: number | null;
  speechStarted: boolean;
  speechStartedAt: number | null;
  lastSpeechAt: number | null;
  lastStrongSpeechAt: number | null;
  silenceStartedAt: number | null;
  pauseNotified: boolean;
  stopRequested: boolean;
  terminationReason: InterviewVadPolicyTerminationReason | null;
};

export type InterviewVadSampleInput = {
  rms: number;
  timestampMs: number;
  terminationEnabled?: boolean;
};

export type InterviewVadSampleClassification = {
  isStrongSpeech: boolean;
  isContinuingSpeech: boolean;
  isQualifyingSpeech: boolean;
  sampleDurationMs: number;
  recordingElapsedMs: number;
  speechWallClockMs: number;
  hasDroppedFromPeak: boolean;
};

export type InterviewVadPolicyTransition = {
  state: InterviewVadPolicyState;
  events: InterviewVadPolicyEvent[];
  sample: InterviewVadSampleClassification;
};

export const createInterviewVadPolicyState = (
  createdAt: number,
): InterviewVadPolicyState => ({
  createdAt,
  peakRms: 0,
  averageRms: 0,
  sampleCount: 0,
  cumulativeSpeechMs: 0,
  cumulativeStrongSpeechMs: 0,
  lastSampleAt: null,
  speechStarted: false,
  speechStartedAt: null,
  lastSpeechAt: null,
  lastStrongSpeechAt: null,
  silenceStartedAt: null,
  pauseNotified: false,
  stopRequested: false,
  terminationReason: null,
});

const requestTermination = (
  state: InterviewVadPolicyState,
  events: InterviewVadPolicyEvent[],
  reason: InterviewVadPolicyTerminationReason,
  timestampMs: number,
  terminationEnabled: boolean,
) => {
  if (!terminationEnabled || state.stopRequested) return state;
  events.push({ type: 'terminate', reason, timestampMs });
  return {
    ...state,
    stopRequested: true,
    terminationReason: reason,
  };
};

export const consumeInterviewVadSample = (
  currentState: InterviewVadPolicyState,
  input: InterviewVadSampleInput,
): InterviewVadPolicyTransition => {
  const rms = Number(input.rms) || 0;
  const timestampMs = Number(input.timestampMs);
  const terminationEnabled = input.terminationEnabled !== false;
  const events: InterviewVadPolicyEvent[] = [];
  const sampleDurationMs = currentState.lastSampleAt
    ? Math.max(
        0,
        Math.min(
          INTERVIEW_VAD_POLICY.maximumCreditedSampleIntervalMs,
          timestampMs - currentState.lastSampleAt,
        ),
      )
    : INTERVIEW_VAD_POLICY.firstSampleDurationMs;
  const sampleCount = currentState.sampleCount + 1;
  let state: InterviewVadPolicyState = {
    ...currentState,
    peakRms: Math.max(currentState.peakRms, rms),
    averageRms: ((currentState.averageRms * currentState.sampleCount) + rms) / sampleCount,
    sampleCount,
    lastSampleAt: timestampMs,
  };
  const recordingElapsedMs = timestampMs - state.createdAt;
  const speechWallClockMs = state.speechStartedAt
    ? timestampMs - state.speechStartedAt
    : 0;
  const isStrongSpeech = rms >= INTERVIEW_VAD_POLICY.speechStartRms;
  const isContinuingSpeech = rms >= INTERVIEW_VAD_POLICY.speechContinueRms;
  const isQualifyingSpeech = isStrongSpeech || (state.speechStarted && isContinuingSpeech);

  if (isStrongSpeech) {
    state = {
      ...state,
      cumulativeStrongSpeechMs: state.cumulativeStrongSpeechMs + sampleDurationMs,
    };
  }
  if (isQualifyingSpeech) {
    state = {
      ...state,
      cumulativeSpeechMs: state.cumulativeSpeechMs + sampleDurationMs,
      lastSpeechAt: timestampMs,
      lastStrongSpeechAt: isStrongSpeech ? timestampMs : state.lastStrongSpeechAt,
      silenceStartedAt: null,
    };

    if (isStrongSpeech && state.pauseNotified) {
      state = { ...state, pauseNotified: false };
      events.push({ type: 'speech_continued', timestampMs });
    }

    if (!state.speechStarted) {
      state = {
        ...state,
        speechStarted: true,
        speechStartedAt: timestampMs,
        lastSpeechAt: timestampMs,
        lastStrongSpeechAt: timestampMs,
      };
      events.push({ type: 'speech_started', timestampMs });
    }

    const lastStrongSpeechAt = state.lastStrongSpeechAt || state.speechStartedAt || timestampMs;
    if (
      state.speechStarted
      && !isStrongSpeech
      && !state.pauseNotified
      && timestampMs - lastStrongSpeechAt >= INTERVIEW_VAD_POLICY.pauseUiMs
    ) {
      state = { ...state, pauseNotified: true };
      events.push({ type: 'speech_paused', timestampMs });
    }
  } else if (state.speechStarted) {
    const lastSpeechAt = state.lastSpeechAt || state.speechStartedAt || timestampMs;
    const silenceMs = timestampMs - lastSpeechAt;
    state = {
      ...state,
      silenceStartedAt: state.silenceStartedAt || lastSpeechAt,
    };

    if (!state.pauseNotified && silenceMs >= INTERVIEW_VAD_POLICY.pauseUiMs) {
      state = { ...state, pauseNotified: true };
      events.push({ type: 'speech_paused', timestampMs });
    }

    if (
      speechWallClockMs >= INTERVIEW_VAD_POLICY.minimumSpeechWallClockMs
      && recordingElapsedMs >= INTERVIEW_VAD_POLICY.minimumRecordingDurationMs
      && silenceMs >= INTERVIEW_VAD_POLICY.endSilenceMs
    ) {
      state = requestTermination(
        state,
        events,
        'end_silence',
        timestampMs,
        terminationEnabled,
      );
    }
  }

  const lastStrongSpeechAt = state.lastStrongSpeechAt || state.speechStartedAt || timestampMs;
  const noClearSpeechMs = timestampMs - lastStrongSpeechAt;
  const peakDropThreshold = Math.max(
    INTERVIEW_VAD_POLICY.speechContinueRms + INTERVIEW_VAD_POLICY.hysteresisRms,
    state.peakRms * INTERVIEW_VAD_POLICY.peakDropRatio,
  );
  const hasDroppedFromPeak = state.peakRms > 0 ? rms <= peakDropThreshold : true;
  if (
    state.speechStarted
    && !state.stopRequested
    && !isStrongSpeech
    && speechWallClockMs >= INTERVIEW_VAD_POLICY.minimumSpeechWallClockMs
    && recordingElapsedMs >= INTERVIEW_VAD_POLICY.minimumRecordingDurationMs
    && noClearSpeechMs >= INTERVIEW_VAD_POLICY.clearSpeechTimeoutMs
    && hasDroppedFromPeak
  ) {
    state = requestTermination(
      state,
      events,
      'clear_speech_timeout',
      timestampMs,
      terminationEnabled,
    );
  }

  if (
    state.speechStarted
    && !state.stopRequested
    && speechWallClockMs >= INTERVIEW_VAD_POLICY.minimumSpeechWallClockMs
    && recordingElapsedMs >= INTERVIEW_VAD_POLICY.hardMaximumRecordingMs
  ) {
    state = requestTermination(
      state,
      events,
      'hard_max_duration',
      timestampMs,
      terminationEnabled,
    );
  }

  return {
    state,
    events,
    sample: {
      isStrongSpeech,
      isContinuingSpeech,
      isQualifyingSpeech,
      sampleDurationMs,
      recordingElapsedMs,
      speechWallClockMs,
      hasDroppedFromPeak,
    },
  };
};

export const evaluateInterviewVadInitialNoSpeech = (
  currentState: InterviewVadPolicyState,
  timestampMs: number,
): Pick<InterviewVadPolicyTransition, 'state' | 'events'> => {
  if (
    currentState.speechStarted
    || currentState.stopRequested
    || timestampMs - currentState.createdAt < INTERVIEW_VAD_POLICY.initialNoSpeechMs
  ) {
    return { state: currentState, events: [] };
  }

  const events: InterviewVadPolicyEvent[] = [];
  const state = requestTermination(
    currentState,
    events,
    'initial_no_speech',
    timestampMs,
    true,
  );
  return { state, events };
};

export const isInterviewVadRecordingLikelySilent = (input: {
  state: InterviewVadPolicyState | null;
  recordingDurationMs: number;
  blobSize: number;
}) => {
  const { state, blobSize } = input;
  if (!state) return blobSize < INTERVIEW_VAD_POLICY.minimumAudioBlobBytes;
  if (state.sampleCount < 3) return true;

  const recordingDurationMs = Math.max(1, input.recordingDurationMs);
  const speechRatio = state.cumulativeSpeechMs / recordingDurationMs;
  const veryLowEnergy = state.peakRms < INTERVIEW_VAD_POLICY.veryLowPeakRms
    && state.averageRms < INTERVIEW_VAD_POLICY.veryLowAverageRms;
  const tinyBlob = blobSize < INTERVIEW_VAD_POLICY.minimumAudioBlobBytes;
  const hasClearSpeech = state.speechStarted
    && state.cumulativeSpeechMs >= INTERVIEW_VAD_POLICY.minimumCumulativeSpeechMs
    && state.cumulativeStrongSpeechMs >= INTERVIEW_VAD_POLICY.minimumCumulativeStrongSpeechMs
    && speechRatio >= INTERVIEW_VAD_POLICY.minimumSpeechRatio
    && state.peakRms >= INTERVIEW_VAD_POLICY.speechStartRms;

  return !hasClearSpeech || veryLowEnergy || tinyBlob;
};
